import { Alert, Button, Space } from 'antd';
import Modal from 'components/Modal';
import Table from 'components/Table';
import { renderDate } from 'components/Table/renderers';
import { DetailField, DetailFields } from 'components/DetailFields';
import { useQuery } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { allIssueColumns } from './columns';
import { useAccessibilityReview } from './context';
import { GET_WEBSITE_CHECK_EVIDENCE, GET_PDF_SCAN_PREVIEW } from './graphql';
import { PageIntroduction, REVIEW_CLASS, TABLE_SCROLL } from './ReviewPage';
import { accessibilityFindingGroups, findingSuiteLabel } from './utils';
import {
  DOCUMENT_PREVIEW_LABEL,
  PAGE_EVIDENCE_LABEL,
  discoveryKindLabel,
  distinctPdfLocations,
  findingRequirementLabel,
  findingEvidenceStatusLabel,
  findingStandardLabel,
  groupedFindingDetails,
  isPdfEvidence,
  latestScanPresentation,
  occurrencesForInventoryPage,
  pageEvidenceFindingGroups,
  pageEvidenceScreenshot,
  pageFromOccurrence,
  pageTypeLabel,
  pdfFindingEvidenceLabel,
  verifiedBoundingBox,
} from './viewModel';
import { hasWcagCriterionHelp, WcagCriteriaDetailField, WcagCriterionHelp } from './WcagCriterionHelp';

export const EXTERNAL_LINK_PROPS = { target: '_blank', rel: 'noopener noreferrer' };

export const ExternalUrl = ({ href, children }) => {
  if (!href) return null;
  return <a href={href} {...EXTERNAL_LINK_PROPS}>{children || href}</a>;
};

const websiteCheckIdForOccurrence = occurrence => occurrence?.id?.match(/^website-check:([^:]+):/)?.[1] || null;

const useWebsiteCheckScreenshot = (occurrence) => {
  const websiteCheckId = websiteCheckIdForOccurrence(occurrence);
  const { data } = useQuery(GET_WEBSITE_CHECK_EVIDENCE, {
    variables: { websiteCheckId: websiteCheckId || '', pageUrl: occurrence?.pageUrl || '' },
    skip: !websiteCheckId || !occurrence?.pageUrl,
  });
  return data?.websiteCheckEvidence || null;
};

const verifiedAnnotations = (screenshot, annotations = []) => (screenshot.pageSize
  ? annotations.filter(annotation => annotation.status === 'position_verified' && verifiedBoundingBox(annotation.boundingBox))
  : []);

const annotationOutline = (focused, borderWidth) => ({
  border: `${borderWidth}px solid #ff4d4f`,
  boxShadow: `0 0 0 ${borderWidth === 4 ? 2 : 1}px #fff, 0 0 0 ${borderWidth === 4 ? 4 : 3}px #ff4d4f`,
  opacity: focused ? 1 : 0.72,
});

const ScreenshotEvidence = ({ screenshot, annotations = [], alt, focusedOccurrenceId, focusedOccurrenceIds, onOpen, unconstrained = false }) => {
  const verified = verifiedAnnotations(screenshot, annotations);
  const focusedIds = focusedOccurrenceIds || (focusedOccurrenceId ? [focusedOccurrenceId] : null);
  const pageWidth = Number(screenshot.pageSize?.width) || 1;
  const pageHeight = Number(screenshot.pageSize?.height) || 1;
  const imageStyle = {
    display: 'block',
    height: 'auto',
    maxWidth: '100%',
    ...(unconstrained ? { maxHeight: 'none', width: '100%' } : { maxHeight: '60vh', width: 'auto' }),
  };
  return (
    <div style={{ display: unconstrained ? 'block' : 'inline-block', maxWidth: '100%', position: 'relative' }}>
      {onOpen ? (
        <Button aria-label="Open full-size evidence" type="text" onClick={onOpen} style={{ cursor: 'zoom-in', height: 'auto', padding: 0, width: unconstrained ? '100%' : 'auto' }}>
          <img src={screenshot.storageUrl} alt={alt} style={imageStyle} />
        </Button>
      ) : <img src={screenshot.storageUrl} alt={alt} style={imageStyle} />}
      {verified.map((annotation) => {
        const { x, y, width, height } = annotation.boundingBox;
        const focused = !focusedIds?.length || focusedIds.includes(annotation.occurrenceId);
        return (
          <span
            key={`${annotation.occurrenceId || 'occurrence'}:${annotation.selector}`}
            title={annotation.selector}
            style={{
              ...annotationOutline(focused, 3),
              boxSizing: 'border-box',
              height: `${(height / pageHeight) * 100}%`,
              left: `${(x / pageWidth) * 100}%`,
              pointerEvents: 'none',
              position: 'absolute',
              top: `${(y / pageHeight) * 100}%`,
              width: `${(width / pageWidth) * 100}%`,
            }}
          >
            {annotation.label && (
              <span style={{ background: '#ff4d4f', borderRadius: '50%', boxShadow: '0 0 0 2px #fff', color: '#fff', display: 'block', fontSize: 11, fontWeight: 'bold', height: 20, left: -10, lineHeight: '20px', position: 'absolute', textAlign: 'center', top: -10, width: 20 }}>
                {annotation.label}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

const EvidenceLightbox = ({ open, onClose, screenshot, annotations, focusedOccurrenceId, focusedOccurrenceIds, title = PAGE_EVIDENCE_LABEL }) => {
  const [zoom, setZoom] = useState(1);
  const viewerRef = useRef(null);
  const verified = verifiedAnnotations(screenshot || {}, annotations);
  const focusedIds = focusedOccurrenceIds || (focusedOccurrenceId ? [focusedOccurrenceId] : null);
  const focused = verified.find(annotation => focusedIds?.includes(annotation.occurrenceId)) || verified[0];
  const pageWidth = Number(screenshot?.pageSize?.width);
  const pageHeight = Number(screenshot?.pageSize?.height);
  const hasPageSize = pageWidth > 0 && pageHeight > 0;
  const focusX = hasPageSize && focused ? (focused.boundingBox.x + (focused.boundingBox.width / 2)) / pageWidth : 0.5;
  const focusY = hasPageSize && focused ? (focused.boundingBox.y + (focused.boundingBox.height / 2)) / pageHeight : 0.5;

  useEffect(() => {
    if (open) setZoom(1);
  }, [open, focusedOccurrenceId, focusedOccurrenceIds]);

  useEffect(() => {
    if (!open || !hasPageSize || !viewerRef.current) return undefined;
    const frame = requestAnimationFrame(() => {
      const viewer = viewerRef.current;
      const scale = (viewer.clientWidth * zoom) / pageWidth;
      viewer.scrollLeft = Math.max(0, (focusX * pageWidth * scale) - (viewer.clientWidth / 2));
      viewer.scrollTop = Math.max(0, (focusY * pageHeight * scale) - (viewer.clientHeight / 2));
    });
    return () => cancelAnimationFrame(frame);
  }, [focusX, focusY, hasPageSize, open, pageHeight, pageWidth, zoom]);

  if (!screenshot) return null;
  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={null}
      fullWidth
      rootClassName={REVIEW_CLASS}
      style={{ maxWidth: 'calc(100vw - 32px)', top: 16 }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          <Button onClick={() => setZoom(value => Math.max(1, value - 0.25))}>Zoom out</Button>
          <Button onClick={() => setZoom(value => Math.min(3, value + 0.25))}>Zoom in</Button>
          <Button onClick={() => setZoom(1)}>Reset</Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
        <div ref={viewerRef} style={{ background: '#161616', height: '78vh', overflow: 'auto', overscrollBehavior: 'contain' }}>
          <div style={{ aspectRatio: hasPageSize ? `${pageWidth} / ${pageHeight}` : undefined, margin: '0 auto', position: 'relative', width: `${zoom * 100}%` }}>
            <img src={screenshot.storageUrl} alt={`Full-size ${title.toLowerCase()}`} style={{ display: 'block', height: hasPageSize ? '100%' : 'auto', width: '100%' }} />
            {verified.map((annotation) => {
              const { x, y, width, height } = annotation.boundingBox;
              const isFocused = !focusedIds?.length || focusedIds.includes(annotation.occurrenceId);
              return (
                <span
                  key={`${annotation.occurrenceId || 'occurrence'}:${annotation.selector}`}
                  style={{
                    ...annotationOutline(isFocused, 4),
                    boxSizing: 'border-box',
                    height: `${(height / pageHeight) * 100}%`,
                    left: `${(x / pageWidth) * 100}%`,
                    pointerEvents: 'none',
                    position: 'absolute',
                    top: `${(y / pageHeight) * 100}%`,
                    width: `${(width / pageWidth) * 100}%`,
                  }}
                >
                  {annotation.label && (
                    <span style={{ background: '#ff4d4f', borderRadius: '50%', boxShadow: '0 0 0 2px #fff', color: '#fff', display: 'block', fontSize: 15, fontWeight: 'bold', height: 28, left: -14, lineHeight: '28px', position: 'absolute', textAlign: 'center', top: -14, width: 28 }}>
                      {annotation.label}
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      </Space>
    </Modal>
  );
};

const EvidenceImagePanel = ({ screenshot, annotations, focusedOccurrenceId, focusedOccurrenceIds, onOpen, alt, missingMessage, missingDescription, title = 'Page evidence' }) => (
  <div style={{ flex: '0 1 30%', minWidth: 240 }}>
    {screenshot?.storageUrl ? (
      <div>
        <strong>{title}</strong>
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <ScreenshotEvidence
            screenshot={screenshot}
            annotations={annotations}
            focusedOccurrenceId={focusedOccurrenceId}
            focusedOccurrenceIds={focusedOccurrenceIds}
            onOpen={onOpen}
            alt={alt}
            unconstrained
          />
        </div>
        <div>
          <small>Open the image for the full-size evidence view.</small>
        </div>
      </div>
    ) : (
      <Alert
        type="warning"
        showIcon
        message={missingMessage}
        description={missingDescription}
      />
    )}
  </div>
);

const EvidenceSummary = ({ children }) => (
  <DetailFields className="accessibility-evidence-summary">{children}</DetailFields>
);

const scanPresentationText = presentation => (
  presentation?.kind === 'completed' && presentation.completedAt
    ? renderDate(presentation.completedAt)
    : (presentation?.text || '—')
);

const ListedValues = ({ items, children }) => {
  const [expanded, setExpanded] = useState(false);
  if (!items?.length) return null;
  const visible = expanded ? items : items.slice(0, 1);
  const extra = items.length - 1;
  return (
    <div>
      {visible.map((item, index) => (
        <div key={index} style={{ marginTop: index ? 8 : 0 }}>{children(item)}</div>
      ))}
      {extra > 0 && (
        <Button type="link" size="small" onClick={() => setExpanded(open => !open)} style={{ height: 'auto', padding: '4px 0 0' }}>
          {expanded ? 'Show less' : `Show more (${extra})`}
        </Button>
      )}
    </div>
  );
};

const GroupedOccurrenceDetails = ({ group, details, pageScreenshot }) => {
  const representative = group.occurrences[0];
  const pdf = isPdfEvidence(representative);
  const { shared, listed } = details;
  const listedInstances = listed.brokenLink || listed.documentPageNumber || listed.cssSelectors || listed.affectedHtml;
  const wcagHelp = hasWcagCriterionHelp({ criterionIds: representative.criterionIds });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <DetailFields>
        <DetailField label="Category">{findingSuiteLabel(representative.suiteType)}</DetailField>
        {representative.suiteType === 'Accessibility' && (
          <DetailField label="Standard or category">{findingStandardLabel(representative)}</DetailField>
        )}
        <DetailField label={representative.suiteType === 'Accessibility' ? 'Provider rule' : 'Finding type'}>{group.ruleId}</DetailField>
        {group.count > 1 && <DetailField label="Findings">{group.count}</DetailField>}
        <DetailField label="Type">{pageTypeLabel(representative.pageType)}</DetailField>
        {representative.suiteType === 'Accessibility' && (
          <>
            <DetailField label="Requirement / relationship">{findingRequirementLabel(representative)}</DetailField>
            <DetailField label="Evidence status">{findingEvidenceStatusLabel(representative)}</DetailField>
          </>
        )}
        {!pdf && (
          <>
            <DetailField label="Viewport">
              {representative.viewport
                ? `${representative.viewport.name} (${representative.viewport.source.replace(/_/g, ' ')})`
                : 'Provider viewport not reported'}
            </DetailField>
            <DetailField label="Captured">{pageScreenshot?.capturedAt || representative.lastSeenAt}</DetailField>
            <DetailField label="Cookie banner">{pageScreenshot?.cookieBannerHandling || 'Not reported'}</DetailField>
          </>
        )}
        {representative.source && <DetailField label="Source">{representative.source}</DetailField>}
        {pdf && shared.documentPageNumber != null && (
          <DetailField label="Document page">{shared.documentPageNumber}</DetailField>
        )}
        {shared.brokenLink && (
          <DetailField label="Broken link">
            <a href={shared.brokenLink.linkUrl} {...EXTERNAL_LINK_PROPS}>{shared.brokenLink.linkUrl}</a>
            {shared.brokenLink.httpStatus ? ` (${shared.brokenLink.httpStatus})` : ''}
          </DetailField>
        )}
      </DetailFields>
      {wcagHelp ? (
        <DetailFields>
          <WcagCriteriaDetailField criterionIds={representative.criterionIds} />
        </DetailFields>
      ) : (shared.description || listed.description) && (
        <DetailFields>
          <DetailField label={pdf ? 'Explanation' : 'Description'}>
            {shared.description || (
              <ListedValues items={listed.description}>{value => value}</ListedValues>
            )}
          </DetailField>
        </DetailFields>
      )}
      {shared.affectedHtml && (
        <DetailFields>
          <DetailField label="Affected HTML">
            <pre style={{ fontSize: 12, lineHeight: 1.5, margin: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{shared.affectedHtml}</pre>
          </DetailField>
        </DetailFields>
      )}
      {shared.cssSelectors && (
        <DetailFields>
          <DetailField label="CSS selector">
            {shared.cssSelectors.map(selector => (
              <pre key={selector} style={{ fontSize: 12, lineHeight: 1.5, margin: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{selector}</pre>
            ))}
          </DetailField>
        </DetailFields>
      )}
      {listedInstances && (
        <DetailFields>
          {listed.brokenLink && (
            <DetailField label="Broken links">
              <ListedValues items={listed.brokenLink}>
                {item => (
                  <span>
                    <a href={item.linkUrl} {...EXTERNAL_LINK_PROPS}>{item.linkUrl}</a>
                    {item.httpStatus ? ` (${item.httpStatus})` : ''}
                  </span>
                )}
              </ListedValues>
            </DetailField>
          )}
          {listed.documentPageNumber && (
            <DetailField label="Document pages">
              <ListedValues items={listed.documentPageNumber}>{page => page}</ListedValues>
            </DetailField>
          )}
          {listed.cssSelectors && (
            <DetailField label="CSS selectors">
              <ListedValues items={listed.cssSelectors}>
                {selectors => selectors.map(selector => (
                  <pre key={selector} style={{ fontSize: 12, lineHeight: 1.5, margin: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{selector}</pre>
                ))}
              </ListedValues>
            </DetailField>
          )}
          {listed.affectedHtml && (
            <DetailField label="Affected HTML">
              <ListedValues items={listed.affectedHtml}>
                {html => (
                  <pre style={{ fontSize: 12, lineHeight: 1.5, margin: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{html}</pre>
                )}
              </ListedValues>
            </DetailField>
          )}
        </DetailFields>
      )}
      {pageScreenshot?.annotationStatus === 'position_unresolved' && (
        <Alert
          type="warning"
          showIcon
          message="The element position could not be verified in this image."
          description="Use the CSS selector and affected HTML to locate the occurrence. Extellio does not draw an unverified marker."
        />
      )}
      {(shared.fixInstructions || listed.fixInstructions) && (
        listed.fixInstructions
          ? (
            <DetailFields>
              <DetailField label="How to fix">
                <ListedValues items={listed.fixInstructions}>{value => value}</ListedValues>
              </DetailField>
            </DetailFields>
          )
          : <Alert type="success" showIcon message={pdf ? 'Remediation' : 'How to fix'} description={shared.fixInstructions} />
      )}
      {!wcagHelp && shared.helpUrl && (
        <a href={shared.helpUrl} {...EXTERNAL_LINK_PROPS}>Learn more</a>
      )}
      {!wcagHelp && listed.helpUrl && (
        <ListedValues items={listed.helpUrl}>
          {url => <a href={url} {...EXTERNAL_LINK_PROPS}>Learn more</a>}
        </ListedValues>
      )}
    </Space>
  );
};

export const PageEvidenceModal = ({ page: selectedPage, occurrenceId, onClose }) => {
  const { result, scope } = useAccessibilityReview();
  const { dataSource } = useDataSource();
  const occurrences = useMemo(
    () => occurrencesForInventoryPage(result, selectedPage),
    [result, selectedPage],
  );
  const groups = useMemo(() => pageEvidenceFindingGroups(occurrences), [occurrences]);
  const isPdf = selectedPage?.pageType === 'PDF';
  const pdfScanningEnabled = scope?.pdfScanningEnabled;
  const scanEvidenceId = isPdf ? selectedPage?.latestPdfScanEvidence?.id : null;
  const { data: previewData } = useQuery(GET_PDF_SCAN_PREVIEW, {
    variables: { dataSourceId: dataSource?.id, scanEvidenceId: scanEvidenceId || '' },
    skip: !dataSource?.id || !scope?.id || !scanEvidenceId,
  });
  const scanPreview = previewData?.dataSource?.accessibilityReviewScopes
    ?.find(({ id }) => id === scope.id)?.pdfScanPreview
    || selectedPage?.latestPdfScanEvidence?.preview
    || null;
  const [focusedGroupId, setFocusedGroupId] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const focusedGroup = groups.find(group => group.id === focusedGroupId)
    || groups.find(group => group.occurrences.some(occurrence => occurrence.id === occurrenceId))
    || groups[0]
    || null;
  const focusedOccurrence = focusedGroup?.occurrences.find(occurrence => occurrence.id === occurrenceId)
    || focusedGroup?.occurrences[0]
    || null;
  const focusedOccurrenceIds = focusedGroup?.occurrences.map(occurrence => occurrence.id) || [];
  const details = useMemo(
    () => groupedFindingDetails(focusedGroup?.occurrences || []),
    [focusedGroup],
  );
  const focusedWebsiteCheckScreenshot = useWebsiteCheckScreenshot(isPdf ? null : focusedOccurrence);
  const pageScreenshot = useMemo(() => pageEvidenceScreenshot({
    pageType: selectedPage?.pageType,
    occurrences,
    websiteCheckScreenshot: focusedWebsiteCheckScreenshot,
    scanPreview,
    focusedOccurrence: isPdf ? focusedOccurrence : null,
  }), [focusedOccurrence, focusedWebsiteCheckScreenshot, isPdf, occurrences, scanPreview, selectedPage]);
  const pdfPageLabel = isPdf ? pdfFindingEvidenceLabel(focusedOccurrence) : null;
  const pdfPanelTitle = pdfPageLabel || DOCUMENT_PREVIEW_LABEL;
  const pageAnnotations = useMemo(() => {
    if (isPdf || focusedWebsiteCheckScreenshot) return [];
    return occurrences.flatMap((occurrence, index) => (
      (occurrence.annotations || []).map(annotation => ({
        ...annotation,
        label: index + 1,
        occurrenceId: occurrence.id,
      }))
    ));
  }, [focusedWebsiteCheckScreenshot, isPdf, occurrences]);

  useEffect(() => {
    const match = groups.find(group => group.occurrences.some(occurrence => occurrence.id === occurrenceId));
    setFocusedGroupId(match?.id || groups[0]?.id || null);
  }, [selectedPage?.id, occurrenceId]);

  useEffect(() => {
    setLightboxOpen(false);
  }, [focusedGroupId, selectedPage?.id]);

  const canonicalUrl = selectedPage?.pageUrl || selectedPage?.locations?.[0]?.requestedUrl || '';
  const scanPresentation = selectedPage ? latestScanPresentation(selectedPage, {
    pdfScanningEnabled,
    scanHistory: scope?.scanHistory,
  }) : null;

  return (
    <Modal
      title={selectedPage ? `Document evidence: ${selectedPage.pageTitle || canonicalUrl}` : ''}
      open={Boolean(selectedPage)}
      onCancel={onClose}
      footer={null}
      fullWidth={900}
      rootClassName={REVIEW_CLASS}
    >
      {selectedPage && (
        <Space direction="vertical" size="large" style={{ boxSizing: 'border-box', padding: '0 16px', width: '100%' }}>
          <EvidenceSummary>
            <DetailField label="Type">{pageTypeLabel(selectedPage.pageType)}</DetailField>
            <DetailField label="Issues">{occurrences.length}</DetailField>
            <DetailField label="Latest scan">{scanPresentationText(scanPresentation)}</DetailField>
            <DetailField label={isPdf ? 'PDF' : 'HTML page'}>
              {canonicalUrl ? <ExternalUrl href={canonicalUrl} /> : '—'}
            </DetailField>
            {isPdf && distinctPdfLocations(selectedPage.locations, canonicalUrl).map(location => (
              <DetailField key={location.id || location.requestedUrl} label="Alternate PDF URL">
                <span>
                  <ExternalUrl href={location.requestedUrl} />
                  {location.finalUrl && location.finalUrl !== location.requestedUrl ? (
                    <>
                      {' → '}
                      <ExternalUrl href={location.finalUrl} />
                    </>
                  ) : ''}
                </span>
              </DetailField>
            ))}
            {isPdf && (
              <DetailField label="Linked from">
                {(selectedPage.referringPages || []).length
                  ? selectedPage.referringPages.map(referrer => (
                    <div key={`${referrer.pageId || referrer.url}:${referrer.discoveryKind}`}>
                      <ExternalUrl href={referrer.url} />
                      {` (${discoveryKindLabel(referrer.discoveryKind)})`}
                    </div>
                  ))
                  : '—'}
              </DetailField>
            )}
          </EvidenceSummary>
          <div style={{ alignItems: 'flex-start', display: 'flex', flexWrap: 'wrap', gap: 36 }}>
            <div style={{ flex: '1 1 620px', minWidth: 0 }}>
              <strong>{occurrences.length ? 'Issues in this document' : 'No issues in this document'}</strong>
              <Space direction="vertical" size="small" style={{ marginTop: 8, width: '100%' }}>
                {groups.map((group) => (
                  <Button
                    key={group.id}
                    type={focusedGroup?.id === group.id ? 'primary' : 'default'}
                    onClick={() => setFocusedGroupId(group.id)}
                    style={{ alignItems: 'flex-start', display: 'flex', height: 'auto', justifyContent: 'flex-start', padding: '8px 12px', textAlign: 'left', whiteSpace: 'normal', width: '100%' }}
                  >
                    {`${findingSuiteLabel(group.suiteType)} · ${group.ruleId}${group.count > 1 ? ` (${group.count})` : ''}`}
                  </Button>
                ))}
              </Space>
              {focusedGroup && (
                <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 24, paddingTop: 16 }}>
                  <GroupedOccurrenceDetails
                    group={focusedGroup}
                    details={details}
                    pageScreenshot={pageScreenshot}
                  />
                </div>
              )}
            </div>
            <EvidenceImagePanel
              screenshot={pageScreenshot}
              annotations={isPdf ? [] : pageAnnotations}
              focusedOccurrenceIds={isPdf ? null : focusedOccurrenceIds}
              onOpen={() => setLightboxOpen(true)}
              alt={isPdf ? pdfPanelTitle : `Saved evidence for ${canonicalUrl}`}
              title={isPdf ? pdfPanelTitle : PAGE_EVIDENCE_LABEL}
              missingMessage={isPdf
                ? (pdfPageLabel
                  ? `No ${pdfPageLabel.toLowerCase()} is available for this finding.`
                  : 'No document preview is available for this PDF.')
                : 'No Extellio-owned screenshot is available for this page.'}
              missingDescription={isPdf
                ? (pdfPageLabel
                  ? 'The affected page was not captured, or preview failure left it unavailable.'
                  : 'Preview failure does not change the validation result.')
                : 'The individual findings below still include their available evidence.'}
            />
          </div>
          <EvidenceLightbox
            open={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            screenshot={pageScreenshot}
            annotations={isPdf ? [] : pageAnnotations}
            focusedOccurrenceIds={isPdf ? null : focusedOccurrenceIds}
            title={isPdf ? pdfPanelTitle : PAGE_EVIDENCE_LABEL}
          />
        </Space>
      )}
    </Modal>
  );
};

export const EvidenceModals = () => {
  const { selectedOccurrence, setSelectedOccurrence } = useAccessibilityReview();
  return (
    <PageEvidenceModal
      page={pageFromOccurrence(selectedOccurrence)}
      occurrenceId={selectedOccurrence?.id}
      onClose={() => setSelectedOccurrence(null)}
    />
  );
};

export const CriterionIssuesModal = ({ criterion, onClose }) => {
  const { pageType, result, setSelectedOccurrence } = useAccessibilityReview();
  const issues = useMemo(() => accessibilityFindingGroups(result)
    .filter(group => group.criterionIds.includes(criterion?.id))
    .flatMap(group => group.occurrences.map(occurrence => ({
      ...occurrence,
      criterionIds: group.criterionIds,
      classification: group.classification,
      status: occurrence.status || group.status,
      ruleId: group.ruleId,
      suiteType: 'Accessibility',
    }))), [criterion, result]);

  return (
    <Modal
      title={criterion ? `Issues for WCAG ${criterion.id}: ${criterion.title}` : ''}
      open={Boolean(criterion)}
      onCancel={onClose}
      footer={null}
      fullWidth={900}
      rootClassName={REVIEW_CLASS}
    >
      {criterion && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <PageIntroduction title="Current observation evidence">
            Open Evidence to inspect the automated issue or review signal associated with this criterion.
          </PageIntroduction>
          <WcagCriterionHelp criterion={criterion} />
          <Table
            columns={allIssueColumns((occurrence) => {
              onClose();
              setSelectedOccurrence(occurrence);
            }, { pageType, suiteType: 'Accessibility' })}
            dataSource={issues}
            pagination={{ pageSize: 10 }}
            resizable={false}
            scroll={TABLE_SCROLL}
          />
        </Space>
      )}
    </Modal>
  );
};
